import { useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import MultiSelectDropdown from '@/components/ui/MultiSelectDropdown'
import { useOcelMetadata } from '../../../stores/OcelContext'
import LocalFilterPanel from '../../../components/common/LocalFilterPanel'
import TooltipAttributeSettingsField from '../../../components/common/TooltipAttributeSettingsField'

function RadioGroup({ name, value, options, onChange }) {
  return (
    <div className="flex flex-col gap-1.5">
      {options.map(opt => (
        <label key={opt.value} className="flex items-start gap-1.5 text-sm text-gray-700 cursor-pointer leading-snug">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="mt-0.5 flex-shrink-0"
          />
          {opt.label}
        </label>
      ))}
    </div>
  )
}

export default function GanttSettings({
  settings,
  onUpdate,
  onBatchUpdate,
  objectTypes,
  attributes,
  attributesByType,
  activities,
  activeFile,
  localFilters,
  updateLocalFilter,
}) {
  function handleObjectTypeChange(value) {
    onBatchUpdate({
      objectType:          value,
      summaryAttribute:    '',
      taskLabelAttribute:  '',
      taskStartActivity:   [],
      taskEndActivity:     [],
      summaryPlannedStart: '',
      summaryPlannedEnd:   '',
      taskPlannedStart:    '',
      taskPlannedEnd:      '',
      summaryExtraColumns: [],
      taskExtraColumns:    [],
      taskTooltipAttributes: [],
      markerActivities:    [],
    })
  }

  const metadata = useOcelMetadata(activeFile)

  const timestampAttributes = useMemo(() => {
    const typed = new Set()
    for (const et of metadata?.event_types || []) {
      for (const a of et.attributes || []) {
        if ((a.type || '').toLowerCase() === 'timestamp') typed.add(a.name)
      }
    }
    for (const ot of metadata?.object_types_schema || []) {
      for (const a of ot.attributes || []) {
        if ((a.type || '').toLowerCase() === 'timestamp') typed.add(a.name)
      }
    }
    if (typed.size > 0) return [...typed].sort()
    // Fall back to all attribute names when OCEL schema has no explicit types
    const all = new Set([
      ...(metadata?.event_attribute_names || []),
      ...(metadata?.object_attribute_names || []),
    ])
    return [...all].sort()
  }, [metadata])

  const effectiveAttributes = useMemo(() => {
    const schemaAttrs = attributesByType?.[settings.objectType]
    if (schemaAttrs && schemaAttrs.length > 0) return schemaAttrs
    return attributes
  }, [settings.objectType, attributesByType, attributes])

  const noAttributes = effectiveAttributes.length === 0

  return (
    <div className="space-y-6">

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Objekttyp</label>
        <Select value={settings.objectType} onValueChange={handleObjectTypeChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Objekttyp wählen…" />
          </SelectTrigger>
          <SelectContent>
            {objectTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border-t border-gray-200" />

      {noAttributes && (
        <div className="p-2.5 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
          Daten laden, um Attribut-Optionen anzuzeigen. Schließe die Einstellungen, wähle einen Objekttyp und öffne die Einstellungen erneut.
        </div>
      )}

      {/* Summary Attribute */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Summary-Attribut</label>
        <Select
          value={settings.summaryAttribute}
          onValueChange={v => onUpdate('summaryAttribute', v)}
          disabled={noAttributes}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Attribut wählen…" />
          </SelectTrigger>
          <SelectContent>
            {effectiveAttributes.map(attr => (
              <SelectItem key={attr} value={attr}>{attr}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Level — Extra Columns */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Summary-Ebene — Zusatzspalten</label>
        <MultiSelectDropdown
          options={effectiveAttributes}
          selectedValues={Array.isArray(settings.summaryExtraColumns) ? settings.summaryExtraColumns : []}
          onSelectionChange={v => onUpdate('summaryExtraColumns', v)}
          placeholder="Attribute wählen…"
          itemType="Attribute"
          disabled={noAttributes}
        />
      </div>

      {/* Task Label Attribute */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Task-Attribut (Beschriftung)</label>
        <Select
          value={settings.taskLabelAttribute}
          onValueChange={v => onUpdate('taskLabelAttribute', v)}
          disabled={noAttributes}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Attribut wählen…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="object_id">object_id</SelectItem>
            {effectiveAttributes.map(attr => (
              <SelectItem key={attr} value={attr}>{attr}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Task Level — Extra Columns */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Task-Ebene — Zusatzspalten</label>
        <MultiSelectDropdown
          options={effectiveAttributes}
          selectedValues={Array.isArray(settings.taskExtraColumns) ? settings.taskExtraColumns : []}
          onSelectionChange={v => onUpdate('taskExtraColumns', v)}
          placeholder="Attribute wählen…"
          itemType="Attribute"
          disabled={noAttributes}
        />
      </div>

      <div className="border-t border-gray-200" />

      {/* Start Activities */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Start-Aktivität(en)</label>
        <MultiSelectDropdown
          options={activities}
          selectedValues={Array.isArray(settings.taskStartActivity) ? settings.taskStartActivity : []}
          onSelectionChange={v => onUpdate('taskStartActivity', v)}
          placeholder="Aktivitäten wählen…"
          itemType="Aktivitäten"
          disabled={activities.length === 0}
        />
      </div>

      {/* End Activities */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ende-Aktivität(en)</label>
        <MultiSelectDropdown
          options={activities}
          selectedValues={Array.isArray(settings.taskEndActivity) ? settings.taskEndActivity : []}
          onSelectionChange={v => onUpdate('taskEndActivity', v)}
          placeholder="Aktivitäten wählen…"
          itemType="Aktivitäten"
          disabled={activities.length === 0}
        />
      </div>

      {/* Marker Activities */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Aktivitäts-Marker</label>
        <MultiSelectDropdown
          options={activities}
          selectedValues={Array.isArray(settings.markerActivities) ? settings.markerActivities : []}
          onSelectionChange={v => onUpdate('markerActivities', v)}
          placeholder="Aktivitäten wählen…"
          itemType="Aktivitäten"
          disabled={activities.length === 0}
        />
      </div>

      <div className="border-t border-gray-200" />

      {/* Start Fallback */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fallback: fehlendes Start-Event</label>
        <RadioGroup
          name="startFallback"
          value={settings.startFallback}
          onChange={v => onUpdate('startFallback', v)}
          options={[
            { value: 'first', label: 'Erstes vorhandenes Event als Start' },
            { value: 'hide',  label: 'Objekt ausblenden' },
          ]}
        />
      </div>

      {/* End Fallback */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fallback: fehlendes Ende-Event</label>
        <RadioGroup
          name="endFallback"
          value={settings.endFallback}
          onChange={v => onUpdate('endFallback', v)}
          options={[
            { value: 'hide',  label: 'Objekt ausblenden' },
            { value: 'today', label: 'Bis heute' },
            { value: 'last',  label: 'Letztes vorhandenes Event' },
            { value: 'fixed', label: 'Feste Standarddauer nach letztem Event' },
          ]}
        />
        {settings.endFallback === 'fixed' && (
          <div className="mt-2 flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              value={settings.endFallbackDays}
              onChange={e => onUpdate('endFallbackDays', Math.max(1, Number(e.target.value)))}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-500">Tage</span>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200" />

      {/* Duration Unit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Dauer-Einheit</label>
        <Select value={settings.durationUnit} onValueChange={v => onUpdate('durationUnit', v)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="h">Stunden (h)</SelectItem>
            <SelectItem value="d">Tage (d)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border-t border-gray-200" />

      {/* Summary Level — Plan Attributes */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Summary-Ebene — Plan-Attribute</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Geplanter Start</label>
            <select
              value={settings.summaryPlannedStart}
              onChange={e => onUpdate('summaryPlannedStart', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">(keine)</option>
              {timestampAttributes.map(attr => (
                <option key={attr} value={attr}>{attr}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Geplantes Ende</label>
            <select
              value={settings.summaryPlannedEnd}
              onChange={e => onUpdate('summaryPlannedEnd', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">(keine)</option>
              {timestampAttributes.map(attr => (
                <option key={attr} value={attr}>{attr}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Task Level — Plan Attributes */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">Task-Ebene — Plan-Attribute</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Geplanter Start</label>
            <select
              value={settings.taskPlannedStart}
              onChange={e => onUpdate('taskPlannedStart', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">(keine)</option>
              {timestampAttributes.map(attr => (
                <option key={attr} value={attr}>{attr}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Geplantes Ende</label>
            <select
              value={settings.taskPlannedEnd}
              onChange={e => onUpdate('taskPlannedEnd', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">(keine)</option>
              {timestampAttributes.map(attr => (
                <option key={attr} value={attr}>{attr}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200" />

      {/* Task Level — Tooltip Attributes */}
      <TooltipAttributeSettingsField
        attributeOptions={effectiveAttributes}
        selectedAttributes={Array.isArray(settings.taskTooltipAttributes) ? settings.taskTooltipAttributes : []}
        onSelectedAttributesChange={v => onUpdate('taskTooltipAttributes', v)}
        trigger="hover"
        disabled={noAttributes}
        showTopDivider={false}
      />

      <div className="border-t border-gray-200" />

      {/* Date Range (local filter) */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        enabledFilterTypes={['dateRange']}
      />
    </div>
  )
}
